// src/pages/offers/offers.modals.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../../utils/supabase";
import { setOfferAcceptance } from "../../utils/api-offers";
import { createSOFromOfferSafe } from "../../utils/api-services";
import { ArrowUpTrayIcon } from "./offers.components";

/* ========================= helpers ========================= */
function safeFileName(name) {
  return String(name || "archivo")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\w.\-()]/g, "");
}

function extractBaseNumberFromOffer(offer) {
  // Intentamos obtener el "base_number" (ej: 6592) desde distintos campos típicos.
  const direct =
    offer?.base_number ??
    offer?.baseNumber ??
    offer?.base ??
    offer?.number ??
    offer?.oc_base_number ??
    null;

  if (direct != null) {
    const s = String(direct).trim();
    const m = s.match(/\d{3,}/);
    if (m) return m[0];
  }

  const code = String(offer?.code || offer?.oc_number || "").trim();
  // Ej: "CT-6592-00" -> 6592
  const m2 = code.match(/\b(\d{3,})\b/);
  return m2 ? m2[1] : null;
}

/* =============== Modal visor de OC (PDF/Imagen) =============== */
export function OCMediaViewerModal({ open, url, onClose }) {
  if (!open || !url) return null;

  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
  const isPdf = /\.pdf(\?.*)?$/i.test(url);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-6xl h-[85vh] rounded-2xl overflow-hidden bg-card text-card-foreground ring-1 ring-border/50 shadow-industrial-lg"
          >
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-2 bg-surface/80 border-b border-border/40">
              <div className="text-sm font-medium truncate">Orden de compra</div>
              <div className="flex items-center gap-2">
                <a
                  href={url}
                  download
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                  title="Descargar"
                >
                  Descargar
                </a>
                <button
                  onClick={onClose}
                  className="px-2 py-1 rounded-md hover:bg-muted/30"
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="w-full h-full pt-10 bg-background">
              {isImage ? (
                <div className="w-full h-full flex items-center justify-center p-3">
                  <img
                    src={url}
                    alt="Orden de compra"
                    className="max-h-full max-w-full rounded-md shadow"
                    loading="lazy"
                  />
                </div>
              ) : (
                <iframe
                  title="OC Viewer"
                  src={isPdf ? url : url}
                  className="w-full h-full border-0"
                />
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* =============== Modal visor de Remito (PDF/Imagen) =============== */
function RemitoViewerModal({ open, url, title = "Remito", onClose }) {
  if (!open || !url) return null;

  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
  const isPdf = /\.pdf(\?.*)?$/i.test(url);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-6xl h-[85vh] rounded-2xl overflow-hidden bg-card text-card-foreground ring-1 ring-border/50 shadow-industrial-lg"
          >
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-2 bg-surface/80 border-b border-border/40">
              <div className="text-sm font-medium truncate">{title}</div>
              <div className="flex items-center gap-2">
                <a
                  href={url}
                  download
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                  title="Descargar remito"
                >
                  Descargar
                </a>
                <button
                  onClick={onClose}
                  className="px-2 py-1 rounded-md hover:bg-muted/30"
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="w-full h-full pt-10 bg-background">
              {isImage ? (
                <div className="w-full h-full flex items-center justify-center p-3">
                  <img
                    src={url}
                    alt={title}
                    className="max-h-full max-w-full rounded-md shadow"
                    loading="lazy"
                  />
                </div>
              ) : (
                <iframe title={title} src={isPdf ? url : url} className="w-full h-full border-0" />
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* =============== Modal: Remitos escaneados =============== */
export function RemitosManagerModal({ open, offer, onClose, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [osId, setOsId] = useState(null);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);

  const [uploadingRemitoId, setUploadingRemitoId] = useState(null);
  const fileRef = useRef(null);
  const pendingRemitoIdRef = useRef(null);

  // visor
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState("");
  const [viewerTitle, setViewerTitle] = useState("");

  const openViewer = (r) => {
    const label = r.official_number || r.code || "Remito";
    setViewerTitle(label);
    setViewerUrl(r.pdf_url);
    setViewerOpen(true);
  };

  const loadRemitos = async (theOsId) => {
    if (!theOsId) return [];
    try {
      setErr(null);
      const { data, error } = await supabase
        .from("remitos")
        .select("id, os_id, code, official_number, date_out, status, pdf_url")
        .eq("os_id", theOsId)
        .order("date_out", { ascending: false });

      if (error) throw error;
      const rows = data || [];
      setItems(rows);
      return rows;
    } catch (e) {
      console.error("[RemitosManagerModal] loadRemitos:", e);
      setErr(e.message || "No se pudieron cargar los remitos.");
      setItems([]);
      return [];
    }
  };

  // 🔎 Fallback: si por alguna razón el osId resuelto no coincide, buscamos remitos por la vista global.
  // Esto ayuda cuando el RPC devuelve un formato distinto o cuando hay una OS alternativa ligada a la oferta.
  
  // 🔎 Resolver OS por base_number (6592) buscando primero remitos existentes.
  // Esto evita depender 100% del RPC (que a veces devuelve una OS distinta cuando hay múltiples OS con el mismo base_number).
  const resolveOsIdByBaseNumber = async () => {
    const base = extractBaseNumberFromOffer(offer);
    if (!base) return null;

    try {
      // 1) Traemos todas las OS de ese base_number
      const { data: osRows, error: osErr } = await supabase
        .from("service_orders")
        .select("id, code, base_number, created_at")
        .eq("base_number", base)
        .order("created_at", { ascending: false })
        .limit(50);

      if (osErr) throw osErr;
      const osIds = (osRows || []).map((r) => r.id).filter(Boolean);
      if (osIds.length === 0) return null;

      // 2) Vemos si hay remitos para alguna de esas OS (priorizamos la que ya tiene remito)
      const { data: remRows, error: remErr } = await supabase
        .from("remitos")
        .select("id, os_id, code, official_number, date_out, status, pdf_url")
        .in("os_id", osIds)
        .order("date_out", { ascending: false })
        .limit(20);

      if (remErr) throw remErr;

      const list = remRows || [];
      if (list.length > 0) {
        // Encontramos remitos: set osId real y lista
        const realOs = list[0].os_id || null;
        if (realOs) setOsId(realOs);
        setItems(list);
        return realOs;
      }

      // 3) Si no hay remitos todavía, devolvemos la OS más reciente (igual sirve para subir cuando se cree el remito)
      return osIds[0] || null;
    } catch (e) {
      console.error("[RemitosManagerModal] resolveOsIdByBaseNumber:", e);
      return null;
    }
  };

  // 🔎 Fallback: búsqueda tolerante (usa un "needle" numérico tipo 6592 para matchear OS/CC/etc).
  const loadRemitosFallbackBySearchView = async () => {
    try {
      const base = extractBaseNumberFromOffer(offer);
      const needleRaw = String(base || offer?.oc_number || offer?.code || "").trim();
      if (!needleRaw) return [];

      const like = `%${needleRaw}%`;

      const { data: hits, error: hitErr } = await supabase
        .from("v_remitos_search")
        .select("remito_id")
        .or(
          [
            `os_code.ilike.${like}`,
            `cost_center.ilike.${like}`,
            `official_number.ilike.${like}`,
            `remito_code.ilike.${like}`,
            `client_name.ilike.${like}`,
          ].join(",")
        )
        .order("date_out", { ascending: false })
        .limit(20);

      if (hitErr) throw hitErr;

      const remitoIds = (hits || []).map((h) => h.remito_id).filter(Boolean);
      if (remitoIds.length === 0) return [];

      const { data: rows, error: rErr } = await supabase
        .from("remitos")
        .select("id, os_id, code, official_number, date_out, status, pdf_url")
        .in("id", remitoIds)
        .order("date_out", { ascending: false });

      if (rErr) throw rErr;

      const list = rows || [];
      if (list.length) {
        const realOs = list[0].os_id || null;
        if (realOs) setOsId(realOs);
      }
      setItems(list);
      return list;
    } catch (e) {
      console.error("[RemitosManagerModal] fallback search:", e);
      return [];
    }
  };

  useEffect(() => {
    let cancel = false;

    async function resolveAndLoad() {
      if (!open || !offer?.id) return;

      setLoading(true);
      setErr(null);
      setOsId(null);
      setItems([]);

      try {
        // 1) Si ya viene en el objeto offer, lo usamos.
        // (algunas pantallas traen service_order_id/os_id directo)
        const directOs = offer?.os_id || offer?.service_order_id || null;
        let resolved = directOs;

        // 2) Si no viene, resolvemos por RPC.
        if (!resolved) {
          const { data } = await supabase.rpc("get_os_id_for_offer", {
            p_offer_id: offer.id,
          });

          // El RPC puede devolver: {os_id: uuid} ó [{os_id: uuid}] ó directamente un uuid.
          const row = Array.isArray(data) ? data[0] : data;
          resolved = row?.os_id || (typeof row === "string" ? row : null);
        }

        if (!resolved) {
          // Intento extra: resolver por base_number (por si el RPC no devuelve nada o la oferta no trae os_id).
          const baseResolved = await resolveOsIdByBaseNumber();
          resolved = baseResolved;
        }

        if (!resolved) {
          setErr(
            "Esta oferta todavía no tiene una Orden de Servicio creada. Aceptá la oferta para que se genere la OS."
          );
          return;
        }

        if (cancel) return;
        setOsId(resolved);
        let list = await loadRemitos(resolved);

        // 3) Si no encontramos remitos, intentamos resolver la OS real por base_number (ej: 6592).
        // Esto corrige el caso donde el RPC devuelve una OS distinta a la que realmente tiene remitos.
        if (!cancel && (!list || list.length === 0)) {
          const baseResolved = await resolveOsIdByBaseNumber();

          // Si el resolver por base_number nos dio otra OS, recargamos remitos con esa OS.
          if (baseResolved && baseResolved !== resolved) {
            setOsId(baseResolved);
            list = await loadRemitos(baseResolved);
          }
        }

        // 4) Si aún no hay, probamos un fallback por la vista global (más tolerante).
        if (!cancel && (!list || list.length === 0)) {
          await loadRemitosFallbackBySearchView();
        }
      } catch (e) {
        console.error("[RemitosManagerModal] resolveAndLoad:", e);
        if (!cancel) setErr(e.message || "No se pudo resolver la OS de la oferta.");
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    resolveAndLoad();
    return () => {
      cancel = true;
    };
  }, [open, offer?.id]);

  const openUploadFor = (remitoId) => {
    pendingRemitoIdRef.current = remitoId;
    setErr(null);
    fileRef.current?.click?.();
  };

  const onFileSelected = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    const remitoId = pendingRemitoIdRef.current;
    pendingRemitoIdRef.current = null;

    if (!file || !remitoId || !osId) return;

    try {
      setUploadingRemitoId(remitoId);
      setErr(null);

      const path = `${osId}/${remitoId}/${Date.now()}-${safeFileName(file.name)}`;

      const { error: upErr } = await supabase.storage
        .from("remitos")
        .upload(path, file, {
          contentType: file.type || undefined,
          upsert: false,
        });

      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from("remitos").getPublicUrl(path);
      const publicUrl = pub?.publicUrl;
      if (!publicUrl) throw new Error("No se pudo obtener la URL pública del remito.");

      const { error: updErr } = await supabase
        .from("remitos")
        .update({ pdf_url: publicUrl, updated_at: new Date().toISOString() })
        .eq("id", remitoId);

      if (updErr) throw updErr;

      await loadRemitos(osId);
      onChanged?.();
    } catch (e) {
      console.error("[RemitosManagerModal] upload:", e);
      setErr(e.message || "No se pudo subir el remito.");
    } finally {
      setUploadingRemitoId(null);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[98] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={onFileSelected}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-3xl rounded-2xl overflow-hidden bg-card text-card-foreground ring-1 ring-border/50 shadow-industrial-lg"
          >
            <div className="px-5 py-4 border-b border-border/40 bg-surface/50 flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-foreground">
                  Remitos (escaneados)
                </div>
                <div className="text-xs text-muted mt-1">
                  {offer?.code} · {offer?.client?.name || "—"}
                </div>
                {osId ? (
                  <div className="text-[11px] text-muted mt-1">
                    OS: <span className="text-foreground/80">{osId}</span>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => (osId ? loadRemitos(osId) : null)}
                  className="px-3 py-1.5 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                  disabled={!osId || loading}
                  title="Refrescar lista"
                >
                  Refrescar
                </button>
                <button
                  onClick={onClose}
                  className="px-2 py-1 rounded-md hover:bg-muted/30"
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-5">
              {loading ? (
                <div className="text-sm text-muted">Cargando…</div>
              ) : err ? (
                <div className="text-sm text-rose-300">{err}</div>
              ) : items.length === 0 ? (
                <div className="text-sm text-muted">
                  No hay remitos asociados a esta OS.
                  <div className="text-xs text-muted mt-1">
                    (Primero generá el remito en Operaciones / Remitos, y luego subí el escaneo aquí)
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((r) => {
                    const label = r.official_number || r.code || "Remito";
                    const date = r.date_out ? String(r.date_out) : "—";
                    const hasUrl = !!r.pdf_url;

                    return (
                      <div
                        key={r.id}
                        className="rounded-xl border border-border/40 bg-surface/40 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground truncate">
                            {label}
                          </div>
                          <div className="text-xs text-muted mt-1">
                            Fecha: {date} · Estado: {r.status || "—"}
                          </div>

                          {/* ✅ ya NO mostramos la URL */}
                          {hasUrl ? (
                            <div className="text-xs text-emerald-300 mt-2">
                              Archivo cargado ✅
                            </div>
                          ) : (
                            <div className="text-xs text-amber-300 mt-2">
                              Sin escaneo cargado
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => openUploadFor(r.id)}
                            disabled={uploadingRemitoId === r.id}
                            className="px-3 py-2 rounded-md border border-amber-500/50 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 transition-colors inline-flex items-center gap-2"
                            title="Subir PDF/imagen escaneada"
                          >
                            <ArrowUpTrayIcon className="h-4 w-4" />
                            {uploadingRemitoId === r.id ? "Subiendo…" : "Subir"}
                          </motion.button>

                          {hasUrl && (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => openViewer(r)}
                              className="px-3 py-2 rounded-md border border-slate-500/40 text-slate-200 hover:bg-slate-500/10 transition-colors"
                              title="Ver remito"
                            >
                              Ver
                            </motion.button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-5 flex items-center justify-end gap-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="btn-ghost"
                >
                  Cerrar
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* visor (modal encima del manager) */}
          <RemitoViewerModal
            open={viewerOpen}
            url={viewerUrl}
            title={viewerTitle}
            onClose={() => {
              setViewerOpen(false);
              setViewerUrl("");
              setViewerTitle("");
            }}
          />
        </div>
      )}
    </AnimatePresence>
  );
}

/* =============== Modal: Aceptar oferta =============== */
export function AcceptModal({ open, onClose, offer, onAccepted }) {
  const [via, setVia] = useState("oc");
  const [ocNumber, setOcNumber] = useState("");

  useEffect(() => {
    if (open) {
      setVia(offer?.accepted_via || "oc");
      setOcNumber(offer?.oc_number || "");
    }
  }, [open, offer]);

  if (!open) return null;

  const save = async () => {
    try {
      await setOfferAcceptance(offer.id, {
        via,
        oc_number: via === "oc" ? ocNumber || null : null,
      });
      try {
        await createSOFromOfferSafe(offer.id);
      } catch (e) {
        console.warn("createSOFromOfferSafe:", e?.message || e);
      }
      onAccepted?.();
      onClose();
    } catch (e) {
      console.error(e);
      alert("No se pudo marcar como aceptada.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[71] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-lg rounded-2xl bg-card text-card-foreground shadow-industrial-lg ring-1 ring-border/40"
          >
            <div className="px-5 py-4 border-b border-border/40 bg-surface/50">
              <h3 className="text-lg font-semibold text-foreground">
                ¿Cómo fue aceptada la oferta?
              </h3>
              <p className="text-xs text-muted mt-1">
                {offer?.code} · {offer?.client?.name}
              </p>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground">
                  Vía
                </label>
                <select
                  className="field-lg mt-1"
                  value={via}
                  onChange={(e) => setVia(e.target.value)}
                >
                  <option value="oc">Orden de compra</option>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="phone">Teléfono</option>
                  <option value="walkin">Traído al laboratorio</option>
                </select>
              </div>

              {via === "oc" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <label className="text-sm font-medium text-foreground">
                    Nº de OC (cliente)
                  </label>
                  <input
                    className="field-lg mt-1"
                    placeholder="Ej. OC-1234/2025"
                    value={ocNumber}
                    onChange={(e) => setOcNumber(e.target.value)}
                  />
                  <div className="mt-1 text-xs text-muted">
                    Si la OC todavía no llegó, podés dejar este dato vacío y cargarlo después desde el botón OC.
                  </div>
                </motion.div>
              )}

              <div className="text-xs text-muted">
                * Al guardar, se marcará <b>Aceptada</b> y se intentará crear la{" "}
                <b>OS</b> (una por oferta).
              </div>
            </div>

            <div className="px-5 py-4 border-t border-border/40 flex items-center justify-end gap-2 bg-surface/30">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="btn-ghost"
              >
                Cancelar
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={save}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-md"
              >
                Guardar
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* =============== Modal: Rechazar oferta =============== */
export function RejectModal({ open, onClose, offer, currentUserId, onRejected }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason(offer?.rejection_reason || "");
  }, [open, offer]);

  if (!open) return null;

  const save = async () => {
    const trimmed = (reason || "").trim();
    if (!trimmed) {
      alert("Por favor, indica el motivo del rechazo.");
      return;
    }
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("offers")
        .update({
          status: "rejected",
          rejection_reason: trimmed,
          rejected_at: now,
          rejected_by: currentUserId || null,
          updated_at: now,
        })
        .eq("id", offer.id);

      if (error) throw error;

      onRejected?.();
      onClose();
    } catch (e) {
      console.error(e);
      alert("No se pudo marcar como rechazada.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[72] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-lg rounded-2xl bg-card text-card-foreground shadow-industrial-lg ring-1 ring-border/40"
          >
            <div className="px-5 py-4 border-b border-border/40 bg-surface/50">
              <h3 className="text-lg font-semibold text-foreground">
                Motivo del rechazo
              </h3>
              <p className="text-xs text-muted mt-1">
                {offer?.code} · {offer?.client?.name}
              </p>
            </div>

            <div className="p-5 space-y-3">
              <label className="text-sm font-medium text-foreground">
                Explicá brevemente por qué se rechaza la oferta:
              </label>
              <textarea
                className="field-lg mt-1 min-h-[120px]"
                placeholder="Ej. El cliente eligió otro proveedor / precios fuera de presupuesto / cambios en el alcance, etc."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="text-xs text-muted">
                * Se guardará en <b>rejection_reason</b>, junto con{" "}
                <b>rejected_at</b> y <b>rejected_by</b>.
              </p>
            </div>

            <div className="px-5 py-4 border-t border-border/40 flex items-center justify-end gap-2 bg-surface/30">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="btn-ghost"
              >
                Cancelar
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={save}
                className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors shadow-md"
              >
                Rechazar oferta
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* =============== Modal global: lista todos los remitos con filtro =============== */
export function RemitosSearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [rows, setRows] = useState([]);

  // visor
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState("");
  const [viewerTitle, setViewerTitle] = useState("");

  const openViewer = (r) => {
    const label = r.official_number || r.remito_code || "Remito";
    setViewerTitle(label);
    setViewerUrl(r.pdf_url);
    setViewerOpen(true);
  };

  const load = async (search) => {
    try {
      setLoading(true);
      setErr(null);

      let query = supabase
        .from("v_remitos_search")
        .select(
          "remito_id, remito_code, official_number, date_out, status, pdf_url, client_name, os_code, cost_center"
        )
        .order("date_out", { ascending: false })
        .limit(50);

      const s = (search || "").trim();
      if (s) {
        const like = `%${s}%`;
        query = query.or(
          [
            `remito_code.ilike.${like}`,
            `official_number.ilike.${like}`,
            `client_name.ilike.${like}`,
            `os_code.ilike.${like}`,
            `cost_center.ilike.${like}`,
          ].join(",")
        );
      }

      const { data, error } = await query;
      if (error) throw error;

      setRows(data || []);
    } catch (e) {
      console.error("[RemitosSearchModal] load:", e);
      setErr(e.message || "No se pudieron cargar los remitos.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // carga inicial al abrir
  useEffect(() => {
    if (open) load("");
  }, [open]);

  // “debounce” simple para el filtro
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-5xl rounded-2xl overflow-hidden bg-card text-card-foreground ring-1 ring-border/50 shadow-industrial-lg"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-border/40 bg-surface/50 flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-foreground">
                  Remitos
                </div>
                <div className="text-xs text-muted mt-1">
                  Buscar por Nº remito, cliente, OS o Centro de costo
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => load(q)}
                  className="px-3 py-1.5 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                  disabled={loading}
                >
                  Refrescar
                </button>
                <button
                  onClick={onClose}
                  className="px-2 py-1 rounded-md hover:bg-muted/30"
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Filtro */}
            <div className="p-5 border-b border-border/30">
              <input
                className="field-lg"
                placeholder="Ej: 0001-00006325 / BIOLATINA / OS-2025-0123 / 2025"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {err ? (
                <div className="text-sm text-rose-300 mt-2">{err}</div>
              ) : null}
            </div>

            {/* Lista */}
            <div className="p-5 max-h-[60vh] overflow-auto">
              {loading ? (
                <div className="text-sm text-muted">Cargando…</div>
              ) : rows.length === 0 ? (
                <div className="text-sm text-muted">Sin resultados.</div>
              ) : (
                <div className="space-y-3">
                  {rows.map((r) => {
                    const label = r.official_number || r.remito_code || "Remito";
                    const hasUrl = !!r.pdf_url;

                    return (
                      <div
                        key={r.remito_id}
                        className="rounded-xl border border-border/40 bg-surface/40 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground truncate">
                            {label}
                          </div>
                          <div className="text-xs text-muted mt-1">
                            Cliente: {r.client_name || "—"} · OS:{" "}
                            {r.os_code || "—"} · CC: {r.cost_center || "—"} ·
                            Fecha: {r.date_out || "—"} · Estado: {r.status || "—"}
                          </div>

                          {hasUrl ? (
                            <div className="text-xs text-emerald-300 mt-2">
                              Archivo cargado ✅
                            </div>
                          ) : (
                            <div className="text-xs text-amber-300 mt-2">
                              Sin escaneo cargado
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {hasUrl && (
                            <>
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => openViewer(r)}
                                className="px-3 py-2 rounded-md border border-slate-500/40 text-slate-200 hover:bg-slate-500/10 transition-colors"
                              >
                                Ver
                              </motion.button>

                              <a
                                href={r.pdf_url}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-2 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                              >
                                Descargar
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-border/30 flex justify-end bg-surface/30">
              <button onClick={onClose} className="btn-ghost">
                Cerrar
              </button>
            </div>
          </motion.div>

          {/* ✅ visor modal (reusa el mismo patrón) */}
          <RemitoViewerModal
            open={viewerOpen}
            url={viewerUrl}
            title={viewerTitle}
            onClose={() => {
              setViewerOpen(false);
              setViewerUrl("");
              setViewerTitle("");
            }}
          />
        </div>
      )}
    </AnimatePresence>
  );
}
